// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useState } from 'react';
import { Alert, Button, Checkbox, TextInput } from '@loa/ui-components';
import { croApi } from '../../api/croApi';
import type { CroSearchResult } from '../../api/croApi';

interface CroRegistrySearchPanelProps {
  /** The client's ClientProfile id the confirmed matches are recorded against. */
  clientProfileId: string;
  /** The relationship (profile) id — denormalised so the dashboard can aggregate. */
  profileId?: string;
  initialFirstName?: string;
  initialLastName?: string;
  initialBirthYear?: string;
  initialAddress?: string;
  /** Called after matches are confirmed, with how many were confirmed. */
  onConfirmed?: (confirmedCount: number) => void;
}

/**
 * Search the company registry for a single client, select the candidate people
 * who are that client (multiple), and confirm them. Embeddable (no modal chrome)
 * so it can be stacked per flagged client in the relationship-creation Corporate
 * Holdings step. Confirming records the client↔person matches (carrying the
 * relationship id); holdings then derive from the cached records.
 */
export function CroRegistrySearchPanel({
  clientProfileId,
  profileId,
  initialFirstName,
  initialLastName,
  initialBirthYear,
  initialAddress,
  onConfirmed,
}: CroRegistrySearchPanelProps) {
  const [firstName, setFirstName] = useState(initialFirstName ?? '');
  const [lastName, setLastName] = useState(initialLastName ?? '');
  const [birthYear, setBirthYear] = useState(initialBirthYear ?? '');
  const [address, setAddress] = useState(initialAddress ?? '');
  const [result, setResult] = useState<CroSearchResult | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [confirmedCount, setConfirmedCount] = useState<number | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const runSearch = () => {
    setBusy(true);
    setErr(null);
    setConfirmedCount(null);
    croApi.search({ firstName, lastName, birthYear: birthYear || undefined, address: address || undefined, clientProfileId })
      .then((res) => { setResult(res); setSelected(new Set()); })
      .catch((e) => setErr(e instanceof Error ? e.message : String(e)))
      .finally(() => setBusy(false));
  };

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) { next.delete(id); } else { next.add(id); }
      return next;
    });
  };

  const confirmSelected = () => {
    if (!result || selected.size === 0) { return; }
    setBusy(true);
    setErr(null);
    const count = selected.size;
    croApi.confirm(result.id, clientProfileId, Array.from(selected), [], profileId)
      .then(() => { setConfirmedCount(count); onConfirmed?.(count); })
      .catch((e) => setErr(e instanceof Error ? e.message : String(e)))
      .finally(() => setBusy(false));
  };

  return (
    <div className="flex flex-col gap-md">
      {err && <Alert variant="error">{err}</Alert>}

      <div className="grid grid-cols-2 gap-sm">
        <TextInput label="First name" value={firstName} onChange={(e) => setFirstName(e.target.value)} fluid />
        <TextInput label="Last name" value={lastName} onChange={(e) => setLastName(e.target.value)} fluid />
        <TextInput label="Birth year" value={birthYear} onChange={(e) => setBirthYear(e.target.value)} fluid />
        <TextInput label="Address" value={address} onChange={(e) => setAddress(e.target.value)} fluid />
      </div>
      <div>
        <Button variant="filled" size="md" onClick={runSearch}
                disabled={busy || firstName.trim().length < 2 || lastName.trim().length < 2}>
          {busy && !result ? 'Searching…' : 'Search company registry'}
        </Button>
      </div>

      {confirmedCount != null && (
        <Alert variant="success">
          {confirmedCount} {confirmedCount === 1 ? 'person' : 'people'} confirmed. Their corporate
          holdings will appear on the relationship dashboard.
        </Alert>
      )}

      {result && confirmedCount == null && (
        <div className="flex flex-col gap-xs">
          <p className="text-body-03 text-grey-04">
            {result.candidateCount} candidate{result.candidateCount === 1 ? '' : 's'}
            {result.servedFromCache ? ' (from recent search)' : ''}. Select the people who are this client.
          </p>
          {result.candidates.length === 0 ? (
            <Alert variant="info">No matches. Try adjusting the name or address.</Alert>
          ) : (
            <>
              <ul className="flex flex-col gap-xs overflow-auto">
                {result.candidates.map((c) => (
                  <li key={c.providerPersonId}
                      className="flex flex-row items-center gap-sm border border-grey-06 rounded-input p-sm">
                    <Checkbox checked={selected.has(c.providerPersonId)}
                              onChange={() => toggle(c.providerPersonId)} />
                    <div className="flex flex-col">
                      <span className="text-body-02 font-semibold">{c.name}</span>
                      <span className="text-body-03 text-grey-04">
                        {[c.dob, c.address, c.county].filter(Boolean).join(' · ') || 'No further detail'}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
              <div className="flex flex-row justify-end">
                <Button variant="filled" size="md" onClick={confirmSelected} disabled={busy || selected.size === 0}>
                  Confirm {selected.size > 0 ? `(${selected.size})` : ''}
                </Button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
